import { computed, inject, onBeforeUnmount, provide, ref, toRaw } from 'vue';

import { useProxyModel } from '@components/composable';
import { getUid, propsFactory } from '@components/utils';

import { listOpenStrategy, multipleOpenStrategy, singleOpenStrategy } from './openStrategies';
import {
  classicSelectStrategy,
  independentSelectStrategy,
  independentSingleSelectStrategy,
  leafSelectStrategy,
  leafSingleSelectStrategy
} from './selectStrategies';

import type { TOpenStrategyProp } from './openStrategies';
import type { TSelectStrategyProp } from './selectStrategies';
import type { Ref, InjectionKey, PropType, SetupContext } from 'vue';

export type TNestedProvide = {
  id: Ref<unknown>;
  isGroupActivator?: boolean;
  root: {
    children: Ref<Map<unknown, unknown[]>>;
    parents: Ref<Map<unknown, unknown>>;
    opened: Ref<Set<unknown>>;
    selected: Ref<Map<unknown, 'on' | 'off' | 'indeterminate'>>;
    selectedValues: Ref<unknown[]>;
    register: (id: unknown, parentId: unknown, isGroup?: boolean) => void;
    unregister: (id: unknown) => void;
    open: (id: unknown, value: boolean, event?: Event) => void;
    select: (id: unknown, value: boolean, event?: Event) => void;
    openOnSelect: (id: unknown, value: boolean, event?: Event) => void;
  };
};

export interface INestedProps {
  selectStrategy: TSelectStrategyProp | undefined;
  openStrategy: TOpenStrategyProp | undefined;
  selected: unknown[] | unknown;
  opened: unknown[] | unknown;
  required: boolean;
  'onUpdate:selected'?: ((val: unknown[]) => void) | undefined;
  'onUpdate:opened'?: ((val: unknown[]) => void) | undefined;
}

export const NestedKey: InjectionKey<TNestedProvide> = Symbol.for('cds:nested');

export const emptyNested: TNestedProvide = {
  id: ref(),
  root: {
    children: ref(new Map()),
    parents: ref(new Map()),
    opened: ref(new Set()),
    selected: ref(new Map()),
    selectedValues: ref([]),
    register: () => null,
    unregister: () => null,
    open: () => null,
    openOnSelect: () => null,
    select: () => null
  }
};

export const makeNestedProps = propsFactory(
  {
    /**
     * Стратегия выбора элементов
     */
    selectStrategy: [String, Function] as PropType<TSelectStrategyProp>,
    /**
     * Стратегия открытия элементов
     */
    openStrategy: [String, Function] as PropType<TOpenStrategyProp>,
    /**
     * Массив открытых элементов
     */
    opened: Array as PropType<unknown[]>,
    /**
     * Массив выбранных элементов
     */
    selected: Array as PropType<unknown[]>,
    /**
     * Обязательный выбор
     */
    required: Boolean
  },
  'nested'
);

export const useNested = (props: INestedProps, ctx: SetupContext<any[]>): TNestedProvide['root'] => {
  let isUnmounted = false;
  const children = ref(new Map<unknown, unknown[]>());
  const parents = ref(new Map<unknown, unknown>());
  const opened = useProxyModel(
    props,
    'opened',
    props.opened,
    (value: any) => new Set(value),
    (value) => [...value.values()]
  );

  const selectStrategy = computed(() => {
    if (typeof props.selectStrategy === 'object') return props.selectStrategy;

    switch (props.selectStrategy) {
      case 'single-leaf':
        return leafSingleSelectStrategy(props.required);
      case 'leaf':
        return leafSelectStrategy(props.required);
      case 'independent':
        return independentSelectStrategy(props.required);
      case 'single-independent':
        return independentSingleSelectStrategy(props.required);
      default:
        return classicSelectStrategy(props.required);
    }
  });

  const openStrategy = computed(() => {
    if (typeof props.openStrategy === 'object') return props.openStrategy;

    switch (props.openStrategy) {
      case 'list':
        return listOpenStrategy;
      case 'single':
        return singleOpenStrategy;
      case 'multiple':
      default:
        return multipleOpenStrategy;
    }
  });

  const selected = useProxyModel(
    props,
    'selected',
    props.selected,
    (value: any) => selectStrategy.value.in(value, children.value, parents.value),
    (value) => selectStrategy.value.out(value, children.value, parents.value)
  );

  onBeforeUnmount(() => {
    isUnmounted = true;
  });

  function getPath(id: unknown): unknown[] {
    const path: unknown[] = [];
    let parent: unknown = id;

    while (parent != null) {
      path.unshift(parent);
      parent = parents.value.get(parent);
    }

    return path;
  }

  const nested: TNestedProvide = {
    id: ref(),
    root: {
      children,
      parents,
      opened,
      selected,
      selectedValues: computed(() => {
        const arr = [];

        for (const [key, value] of selected.value.entries()) {
          if (value === 'on') arr.push(key);
        }

        return arr;
      }),
      /**
       * Регистрация элемента
       * @param id
       * @param parentId
       * @param isGroup
       */
      register: (id, parentId, isGroup) => {
        parentId && id !== parentId && parents.value.set(id, parentId);
        isGroup && children.value.set(id, []);

        if (parentId != null) {
          children.value.set(parentId, [...(children.value.get(parentId) || []), id]);
        }
      },
      /**
       * Удаление элемента
       * @param id
       */
      unregister: (id) => {
        if (isUnmounted) return;

        children.value.delete(id);

        const parent = parents.value.get(id);
        if (parent) {
          const list = children.value.get(parent) ?? [];
          children.value.set(
            parent,
            list.filter((child) => child !== id)
          );
          parents.value.delete(id);
          opened.value.delete(id);
        }
      },
      /**
       * Открытие элемента
       * @param id
       * @param value
       * @param event
       */
      open: (id, value, event) => {
        ctx.emit('click:open', { id, value, path: getPath(id), event });

        const newOpened = openStrategy.value.open({
          id,
          value,
          opened: new Set(opened.value),
          children: children.value,
          parents: parents.value,
          event
        });
        newOpened && (opened.value = newOpened);
      },
      /**
       * Открытие элемента при выборе
       * @param id
       * @param value
       * @param event
       */
      openOnSelect: (id, value, event) => {
        const newOpened = openStrategy.value.select({
          id,
          value,
          selected: new Map(selected.value),
          opened: new Set(opened.value),
          children: children.value,
          parents: parents.value,
          event
        });
        newOpened && (opened.value = newOpened);
      },
      /**
       * Выбор элемента
       * @param id
       * @param value
       * @param event
       */
      select: (id, value, event) => {
        ctx.emit('click:select', { id, value, path: getPath(id), event });

        const newSelected = selectStrategy.value.select({
          id,
          value,
          selected: new Map(selected.value),
          children: children.value,
          parents: parents.value,
          event
        });
        newSelected && (selected.value = newSelected);

        nested.root.openOnSelect(id, value, event);
      }
    }
  };

  provide(NestedKey, nested);

  return nested.root;
};

export const useNestedItem = (id: Ref<unknown>, isGroup: boolean) => {
  const parent = inject(NestedKey, emptyNested);

  const uidSymbol = Symbol(getUid());
  const computedId = computed(() => id.value ?? uidSymbol);

  const item = {
    ...parent,
    id: computedId,
    open: (open: boolean, event: Event) => parent.root.open(computedId.value, open, event),
    openOnSelect: (open: boolean, event?: Event) => parent.root.openOnSelect(computedId.value, open, event),
    isOpen: computed(() => parent.root.opened.value.has(computedId.value)),
    parent: computed(() => parent.root.parents.value.get(computedId.value)),
    select: (selected: boolean, event?: Event) => parent.root.select(computedId.value, selected, event),
    isSelected: computed(() => parent.root.selected.value.get(toRaw(computedId.value)) === 'on'),
    isIndeterminate: computed(() => parent.root.selected.value.get(computedId.value) === 'indeterminate'),
    isLeaf: computed(() => !parent.root.children.value.get(computedId.value)),
    isGroupActivator: parent.isGroupActivator
  };

  !parent.isGroupActivator && parent.root.register(computedId.value, parent.id.value, isGroup);

  onBeforeUnmount(() => {
    !parent.isGroupActivator && parent.root.unregister(computedId.value);
  });

  isGroup && provide(NestedKey, item);

  return item;
};

export const useNestedGroupActivator = (): void => {
  const parent = inject(NestedKey, emptyNested);

  provide(NestedKey, { ...parent, isGroupActivator: true });
};
